/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 28th 2024, 5:00:53 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APISERVICEPATHS, useAPIRequest, useAPIMutation, useAPIForm } from "lib/api";
import {
  MediaLibrary,
  MediaLibraryImage,
  MediaLibraryImageValidationSchema,
  MediaLibraryFile,
  MediaLibraryFileValidationSchema,
} from "lib/schemas";
import * as commonUtils from "lib/utils";
import { useDeleteOSItem } from "version-control/services";

const mediaLibrariesAPIPath = APISERVICEPATHS.mediaLibraries;

export const useMediaLibraryAPI = () => ({
  useList: () =>
    useAPIRequest<MediaLibrary[]>({
      apiServicePath: mediaLibrariesAPIPath,
      makeRequestOnInitialMount: false,
    }),
  useFetch: () =>
    useAPIRequest<MediaLibrary>({
      apiServicePath: mediaLibrariesAPIPath,
      makeRequestOnInitialMount: false,
    }),
  useMutate: () =>
    useAPIMutation<MediaLibrary>({
      apiServicePath: mediaLibrariesAPIPath,
    }),
});

export const useMediaLibraryImageAPI = (mediaLibraryId: MediaLibrary["id"]) => {
  const mediaLibraryImageAPIPath = commonUtils.concatPath(
    mediaLibrariesAPIPath,
    `${mediaLibraryId}/images/`
  ) as APISERVICEPATHS;

  return {
    useList: () =>
      useAPIRequest<MediaLibraryImage[]>({
        apiServicePath: mediaLibraryImageAPIPath,
        makeRequestOnInitialMount: false,
      }),

    useFetch: () =>
      useAPIRequest<MediaLibraryImage>({
        apiServicePath: mediaLibraryImageAPIPath,
        makeRequestOnInitialMount: false,
      }),
    useMutate: () =>
      useAPIMutation<MediaLibraryImage>({
        apiServicePath: mediaLibraryImageAPIPath,
      }),
    useAPIForm: (args: {
      id: MediaLibraryImage["id"];
      defaultValues?: MediaLibraryImage;
      onAPISave: (a: MediaLibraryImage) => void;
    }) => {
      return useAPIForm({
        apiServicePath: mediaLibraryImageAPIPath,
        apiFormDataValidationSchema: MediaLibraryImageValidationSchema,
        apiFormDefaultValues: args?.defaultValues,
        apiInitialFetchId: args?.id,
        primaryKey: "id",
        setAPIFormResponse: (data: MediaLibraryImage | MediaLibraryImage[]) => {
          if (Array.isArray(data)) {
            data.forEach((d) => args?.onAPISave(d));
            return;
          }
          args?.onAPISave(data);
        },
        method: args?.id ? "PATCH" : "POST",
      });
    },
    useDelete: () => useDeleteOSItem<MediaLibraryImage>(mediaLibraryImageAPIPath),
  };
};

export const useMediaLibraryFileAPI = (mediaLibraryId: MediaLibrary["id"]) => {
  const mediaLibraryFileAPIPath = commonUtils.concatPath(
    mediaLibrariesAPIPath,
    `${mediaLibraryId}/files/`
  ) as APISERVICEPATHS;

  return {
    useList: () =>
      useAPIRequest<MediaLibraryFile[]>({
        apiServicePath: mediaLibraryFileAPIPath,
        makeRequestOnInitialMount: false,
      }),
    useMutate: () =>
      useAPIMutation<MediaLibraryFile>({
        apiServicePath: mediaLibraryFileAPIPath,
      }),
    useAPIForm: (args: {
      id: MediaLibraryFile["id"];
      defaultValues?: MediaLibraryFile;
      onAPISave: (a: MediaLibraryFile) => void;
    }) => {
      return useAPIForm({
        apiServicePath: mediaLibraryFileAPIPath,
        apiFormDataValidationSchema: MediaLibraryFileValidationSchema,
        apiFormDefaultValues: args?.defaultValues,
        apiInitialFetchId: args?.id,
        primaryKey: "id",
        setAPIFormResponse: (data: MediaLibraryFile | MediaLibraryFile[]) => {
          if (Array.isArray(data)) {
            data.forEach((d) => args?.onAPISave(d));
            return;
          }
          args?.onAPISave(data);
        },
        method: args?.id ? "PATCH" : "POST",
      });
    },
    useDelete: () => useDeleteOSItem<MediaLibraryFile>(mediaLibraryFileAPIPath),
  };
};
